import { useState, useEffect, useCallback } from "react";
import apiClient from "../services/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { TrendingUp, Building } from "lucide-react";
import { formatCurrency } from "../lib/formatters";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent } from "../components/ui/tabs";
import { UnderlineTabsList as TabsList, UnderlineTabsTrigger as TabsTrigger } from "../components/ui/tabs-variants";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { ReportExportButton } from "../components/ui/report-export-button";
import { useShowOffice } from "../hooks/useShowOffice";
const STATUS_MAP = {
  bidding: { color: "bg-pink-100 text-pink-600", label: "Bidding" },
  bid_complete: { color: "bg-blue-100 text-blue-600", label: "Bid Complete" },
  not_bid: { color: "bg-gray-100 text-gray-500", label: "Not Bid" },
  precon: { color: "bg-orange-100 text-orange-600", label: "Precon" },
  active: { color: "bg-green-100 text-green-700", label: "Active" },
  complete: { color: "bg-slate-200 text-slate-600", label: "Complete" },
  archive: { color: "bg-gray-100 text-gray-500", label: "Archive" },
};
const getStatusLabel = (status) => STATUS_MAP[status]?.label || status;

const ProductivityReportPage = () => {
  const { getAuthHeaders } = useAuth();
  const [jobReport, setJobReport] = useState([]);
  const [companyReport, setCompanyReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showOffice } = useShowOffice();

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const [jobRes, companyRes] = await Promise.all([
          apiClient.get(`/reports/productivity?report_type=by_job`),
          apiClient.get(`/reports/productivity?report_type=by_company`),
        ]);
        setJobReport(jobRes.data);
        setCompanyReport(companyRes.data);
      } catch (error) {
        toast.error("Failed to load reports");
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, [getAuthHeaders]);

  // Filter out Office job when showOffice is false
  const filteredJobReport = showOffice 
    ? jobReport 
    : jobReport.filter(r => r.job_name !== "Office");

  const getUsedPercentageColor = (percentage) => {
    if (percentage <= 100) return "text-green-600";
    if (percentage <= 120) return "text-yellow-600";
    return "text-red-600";
  };

  const getUsedPercentageBadge = (percentage) => {
    if (percentage <= 100) return "bg-green-100 text-green-800";
    if (percentage <= 120) return "bg-yellow-100 text-yellow-800";
    return "bg-red-100 text-red-800";
  };

  const getExcelData = useCallback(() => {
    const headers = ["Job", "Status", "Total Hours", "Labor Est.", "Labor Act.", "Labor Used %", "Material Est.", "Material Act.", "Material Used %", "Profit/Loss"];
    const rows = filteredJobReport.map((r) => [
      r.job_name, getStatusLabel(r.status), r.total_hours,
      r.labor_estimate, r.labor_actual, `${r.labor_efficiency}%`,
      r.material_estimate, r.material_actual, `${r.material_efficiency}%`,
      r.profit_loss
    ]);
    const csvContent = [headers, ...rows].map((row) => row.join(",")).join("\n");
    return { filename: "productivity-report", csvContent };
  }, [filteredJobReport]);

  const getPdfContent = useCallback(() => {
    const htmlContent = `
      <h1>Productivity Report</h1>
      <p class="subtitle">Generated ${new Date().toLocaleDateString()}</p>
      <table>
        <thead>
          <tr>
            <th>Job</th><th>Status</th><th class="text-right">Hours</th>
            <th class="text-right">Labor Est.</th><th class="text-right">Labor Act.</th>
            <th class="text-right">Labor Used %</th><th class="text-right">Mat. Est.</th>
            <th class="text-right">Mat. Act.</th><th class="text-right">Mat. Used %</th>
            <th class="text-right">Profit/Loss</th>
          </tr>
        </thead>
        <tbody>
          ${filteredJobReport.map(r => `
            <tr>
              <td>${r.job_name}</td><td>${r.status}</td>
              <td class="text-right">${r.total_hours}</td>
              <td class="text-right">${formatCurrency(r.labor_estimate)}</td>
              <td class="text-right">${formatCurrency(r.labor_actual)}</td>
              <td class="text-right ${r.labor_efficiency <= 100 ? 'green' : r.labor_efficiency <= 120 ? 'yellow' : 'red'}">${r.labor_efficiency}%</td>
              <td class="text-right">${formatCurrency(r.material_estimate)}</td>
              <td class="text-right">${formatCurrency(r.material_actual)}</td>
              <td class="text-right ${r.material_efficiency <= 100 ? 'green' : r.material_efficiency <= 120 ? 'yellow' : 'red'}">${r.material_efficiency}%</td>
              <td class="text-right ${r.profit_loss >= 0 ? 'green' : 'red'}">${formatCurrency(r.profit_loss)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      ${companyReport ? `
        <h2 style="margin-top: 30px;">Company Summary</h2>
        <table style="width: 50%;">
          <tr><td>Total Jobs</td><td class="text-right">${companyReport.total_jobs}</td></tr>
          <tr><td>Total Hours</td><td class="text-right">${companyReport.total_hours}</td></tr>
          <tr><td>Labor Used %</td><td class="text-right ${companyReport.labor_efficiency <= 100 ? 'green' : companyReport.labor_efficiency <= 120 ? 'yellow' : 'red'}">${companyReport.labor_efficiency}%</td></tr>
          <tr><td>Material Used %</td><td class="text-right ${companyReport.material_efficiency <= 100 ? 'green' : companyReport.material_efficiency <= 120 ? 'yellow' : 'red'}">${companyReport.material_efficiency}%</td></tr>
          <tr><td>Total Profit/Loss</td><td class="text-right ${companyReport.total_profit_loss >= 0 ? 'green' : 'red'}">${formatCurrency(companyReport.total_profit_loss)}</td></tr>
        </table>
      ` : ''}
      <p class="footer">V2R Masonry - Productivity Report</p>
    `;
    return { title: "Productivity Report", htmlContent };
  }, [filteredJobReport, companyReport]);

  const getStatusBadge = (status) => {
    const cfg = STATUS_MAP[status] || { color: "bg-gray-400 text-white", label: status };
    return <span className={`badge-status ${cfg.color}`}>{cfg.label}</span>;
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]">
      <div className="animate-pulse text-muted-foreground">Loading...</div>
    </div>;
  }

  return (
    <div className="page-enter space-y-8" data-testid="productivity-report-page">
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-foreground">Productivity Report</h1>
        </div>
        <ReportExportButton
          title="Export Productivity Report"
          getExcelData={getExcelData}
          getPdfContent={getPdfContent}
          data-testid="export-productivity-btn"
        />
      </div>

      <Tabs defaultValue="by_job" className="space-y-6">
        <TabsList data-testid="productivity-tabs">
          <TabsTrigger value="by_job" data-testid="by-job-tab">
            <Building className="w-4 h-4 mr-2" />By Job
          </TabsTrigger>
          <TabsTrigger value="by_company" data-testid="by-company-tab">
            <TrendingUp className="w-4 h-4 mr-2" />Company Overview
          </TabsTrigger>
        </TabsList>

        {/* By Job */}
        <TabsContent value="by_job">
          {filteredJobReport.length === 0 ? (
            <Card className="p-12 text-center">
              <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No job data available</p>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead>Job</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Hours</TableHead>
                      <TableHead className="text-right">Labor Est.</TableHead>
                      <TableHead className="text-right">Labor Act.</TableHead>
                      <TableHead className="text-right">Labor Used %</TableHead>
                      <TableHead className="text-right">Mat. Est.</TableHead>
                      <TableHead className="text-right">Mat. Act.</TableHead>
                      <TableHead className="text-right">Mat. Used %</TableHead>
                      <TableHead className="text-right">Profit/Loss</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredJobReport.map((r) => (
                      <TableRow key={r.job_id} className="table-row">
                        <TableCell className="font-medium">{r.job_name}</TableCell>
                        <TableCell>{getStatusBadge(r.status)}</TableCell>
                        <TableCell className="text-right">{r.total_hours}</TableCell>
                        <TableCell className="text-right">{formatCurrency(r.labor_estimate)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(r.labor_actual)}</TableCell>
                        <TableCell className="text-right">
                          <span className={`badge-status ${getUsedPercentageBadge(r.labor_efficiency)}`}>
                            {r.labor_efficiency}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(r.material_estimate)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(r.material_actual)}</TableCell>
                        <TableCell className="text-right">
                          <span className={`badge-status ${getUsedPercentageBadge(r.material_efficiency)}`}>
                            {r.material_efficiency}%
                          </span>
                        </TableCell>
                        <TableCell className={`text-right font-bold ${r.profit_loss >= 0 ? "text-green-600" : "text-red-600"}`}>
                          {formatCurrency(r.profit_loss)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Company Overview */}
        <TabsContent value="by_company">
          {companyReport && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="stat-card">
                <p className="stat-label">Total Jobs</p>
                <p className="stat-value mt-2">{companyReport.total_jobs}</p>
              </Card>
              <Card className="stat-card">
                <p className="stat-label">Total Hours</p>
                <p className="stat-value mt-2">{companyReport.total_hours}</p>
              </Card>
              <Card className="stat-card">
                <p className="stat-label">Labor Used %</p>
                <p className={`stat-value mt-2 ${getUsedPercentageColor(companyReport.labor_efficiency)}`}>
                  {companyReport.labor_efficiency}%
                </p>
                <p className="text-xs text-muted-foreground mt-1">Actual / Estimated</p>
              </Card>
              <Card className="stat-card">
                <p className="stat-label">Material Used %</p>
                <p className={`stat-value mt-2 ${getUsedPercentageColor(companyReport.material_efficiency)}`}>
                  {companyReport.material_efficiency}%
                </p>
                <p className="text-xs text-muted-foreground mt-1">Actual / Estimated</p>
              </Card>
              <Card className="stat-card">
                <p className="stat-label">Labor Estimate</p>
                <p className="stat-value mt-2">{formatCurrency(companyReport.labor_estimate)}</p>
              </Card>
              <Card className="stat-card">
                <p className="stat-label">Labor Actual</p>
                <p className="stat-value mt-2">{formatCurrency(companyReport.labor_actual)}</p>
              </Card>
              <Card className="stat-card">
                <p className="stat-label">Material Estimate</p>
                <p className="stat-value mt-2">{formatCurrency(companyReport.material_estimate)}</p>
              </Card>
              <Card className="stat-card">
                <p className="stat-label">Material Actual</p>
                <p className="stat-value mt-2">{formatCurrency(companyReport.material_actual)}</p>
              </Card>
              <Card className="stat-card md:col-span-2 lg:col-span-4">
                <p className="stat-label">Total Profit/Loss</p>
                <p className={`stat-value mt-2 ${companyReport.total_profit_loss >= 0 ? "text-green-600" : "text-red-600"}`}>
                  {formatCurrency(companyReport.total_profit_loss)}
                </p>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

    </div>
  );
};

export default ProductivityReportPage;
